import { ArrowLeftRight } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import type { RuntimeWallet } from "@/modules/dashboard/wallets/types/payments-wallets.types";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { RailMaintenanceNotice } from "@/shared/components/payments/RailMaintenanceNotice";
import { useAvailableCorridors } from "@/shared/hooks/useAvailableCorridors";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { formatLimitAmount, isAmountLimitCode } from "@/shared/utils/amountLimits";

import {
  CURRENCY_BY_RUNTIME_CORRIDOR,
  isRuntimeCorridor,
  RAILS_BY_RUNTIME_CORRIDOR,
  railForQuote,
  resolveRuntimeCorridor,
} from "../../constants/dispersions.constants";
import { useDispersions } from "../../hooks/useDispersions";
import type { DispersionQuoteParams } from "../../types/dispersions-api.types";
import type { EnhancedCountry } from "../../types/dispersions-api.types";
import { currencyLabel, currencySymbol } from "../../utils/currencyDisplay";
import { formatDecimalAmount2, parseAmountInputDecimal } from "../../utils/currencyFormatter";
import { FieldSelect, HeroAmountInput } from "./fields";
import { QuoteSummaryCard } from "./QuoteSummaryCard";

// "Nueva dispersión · Paso 1" wired to the real Redux state + PHP quote. The
// interactive inputs use the design-system-aligned primitives (Tailwind + dark
// mode); the summary cards keep the redesign's inline styling.

const QUOTE_DEBOUNCE_DELAY = 500;

// Proper-noun rails render as-is; the generic TRANSFER/CRYPTO buckets are translated at render.
const RAIL_BY_ISO3: Record<string, string> = {
  BRA: "PIX",
  MEX: "SPEI",
  ARG: "CBU/CVU",
  COL: "TRANSFER",
  CHL: "TRANSFER",
  PER: "TRANSFER",
  ECU: "TRANSFER",
  PAN: "TRANSFER",
  PRY: "TRANSFER",
  GTM: "TRANSFER",
  CRY: "CRYPTO",
  EUR: "SEPA",
};

interface Step1AmountConversionPageProps {
  countries: EnhancedCountry[];
  errors?: Record<string, string>;
  touched?: Record<string, boolean>;
  onBlur?: (field: string) => void;
  isOriginLocked?: boolean;
}

// React.memo: the step subscribes to Redux itself, so this only skips re-renders caused by page-local
// state (touched, modals) — its props (memoized errors, stable onBlur) keep identity between those.
export const Step1AmountConversionPage: React.FC<Step1AmountConversionPageProps> = React.memo(
  function Step1AmountConversionPage({ countries, errors = {}, isOriginLocked = false }) {
    const corridors = useAvailableCorridors();
    const {
      fetchWalletList,
      runtimeWalletsByCurrency,
      availableWalletCurrencies,
      isWalletListLoading,
      quoteData,
      quoteLoading,
      quoteError,
      quoteErrorCode,
      lastChangedAmountField,
      fetchQuote,
      formData,
      updateField,
      clearRecipientDataForCorridorChange,
      clearQuoteData,
      fetchRuntimeWallet,
      getRuntimeWalletForCurrency,
      getRuntimeWalletError,
      isRuntimeWalletLoading,
    } = useDispersions();
    const { t, activeLanguage } = useTranslations("dispersions");
    const { t: tMaintenance } = useTranslations("railMaintenance");
    const { user, isInternalUser } = useAuth();

    const [balanceSpin, setBalanceSpin] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
      return () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
      };
    }, []);

    // The origin is a WALLET (a currency), the destination a corridor (a country).
    // See `DispersionFormData` for why the two sides are modelled differently.
    const originCurrency = formData.origin_currency;
    const dest = countries.find((c) => c.code === formData.destination) ?? null;
    const isCrossCurrency = !!originCurrency && !!dest && originCurrency !== dest.currency;

    // The balance shown and validated is the org's LEDGER wallet in the ORIGIN
    // currency — the one the payout debits. No corridor gate: any activated wallet
    // can fund a dispersion now, and the runtime converts when the currencies
    // differ. Org members are pinned to their org server-side (same as
    // `useRuntimeWallets`), so we gate on user type — NOT on a client-side
    // organization id that /auth/me may not carry. INTERNAL staff have no
    // organization of their own, so they get no balance panel and no local check.
    const usesRuntimeBalance = !isInternalUser && !!user;
    const runtimeWallet =
      usesRuntimeBalance && originCurrency ? getRuntimeWalletForCurrency(originCurrency) : undefined;

    // Loads the origin selector's options for EVERY authenticated user, not just
    // the ones that get a balance panel: INTERNAL staff have no wallets of their
    // own and fall back to the activation catalog, which this same request is what
    // fetches. Gating it on `usesRuntimeBalance` left that fallback permanently
    // empty and the selector showing "no results". Cached in the thunk, so
    // mounting step 1 repeatedly costs nothing.
    useEffect(() => {
      if (user) void fetchWalletList();
    }, [user, fetchWalletList]);

    const amountError = errors.amount_origin;
    const destinationError = errors.amount_destination;
    // The beneficiary is not known on this step, so the hint is the envelope of both payer kinds for the
    // corridor's selected rail; nothing is shown while the catalog is unknown or the corridor has no limits.
    const limitHint = (() => {
      const corridor = resolveRuntimeCorridor(formData.destination);
      if (!corridor) return null;
      const currency = CURRENCY_BY_RUNTIME_CORRIDOR[corridor];
      const bounds = corridors.limitsFor(
        railForQuote(corridor, formData.recipientFields),
        corridor,
        currency,
        "PAYOUT",
      )?.any;
      if (!bounds) return null;
      const format = (value: string) => formatLimitAmount(value, currency, activeLanguage);
      if (bounds.min_amount && bounds.max_amount) {
        return t("step1_limit_hint_range", {
          min: format(bounds.min_amount),
          max: format(bounds.max_amount),
        });
      }
      if (bounds.max_amount) return t("step1_limit_hint_max", { max: format(bounds.max_amount) });
      if (bounds.min_amount) return t("step1_limit_hint_min", { min: format(bounds.min_amount) });
      return null;
    })();
    const railCode = dest ? (RAIL_BY_ISO3[dest.code] ?? "TRANSFER") : null;
    const rail =
      railCode === "TRANSFER" ? t("rail_transfer") : railCode === "CRYPTO" ? t("rail_crypto") : railCode;

    // This view never shows the legacy PHP balance: on error we surface a retry instead of a wrong number,
    // and `null` (org has not activated a wallet in that currency yet) shows 0. Runtime balances are decimal
    // strings (up to 8 dp) formatted by Intl directly — no parseFloat round-trip (no Number arithmetic on
    // ledger amounts).
    const runtimeError = usesRuntimeBalance && originCurrency ? getRuntimeWalletError(originCurrency) : null;
    const runtimeLoading =
      usesRuntimeBalance && originCurrency ? isRuntimeWalletLoading(originCurrency) : false;
    const showBalanceError = usesRuntimeBalance && runtimeWallet === undefined && !!runtimeError;
    const displayBalance = formatDecimalAmount2(
      runtimeWallet ? runtimeWallet.balance_available : "0",
      activeLanguage,
    );

    const handleAmountChange = useCallback(
      (side: "send" | "recv", raw: string) => {
        const value = raw.replace(/[^0-9.,]/g, "");
        const fieldName = side === "send" ? "amount_origin" : "amount_destination";
        updateField(fieldName, value);

        if (debounceRef.current) clearTimeout(debounceRef.current);
        const corridor = resolveRuntimeCorridor(formData.destination);
        // Decimal string end to end: a Number round-trip here would lose precision
        // on the 8-dp amounts the ledger works in.
        const amount = parseAmountInputDecimal(value);
        if (!formData.origin_currency || !corridor || !value || amount === null) return;

        debounceRef.current = setTimeout(() => {
          const params: DispersionQuoteParams = {
            originCurrency: formData.origin_currency,
            destinationCountry: corridor,
            rail: railForQuote(corridor, formData.recipientFields),
          };
          if (side === "send") params.sendAmount = amount;
          else params.receiveAmount = amount;
          fetchQuote(params);
        }, QUOTE_DEBOUNCE_DELAY);
      },
      [formData.origin_currency, formData.destination, formData.recipientFields, updateField, fetchQuote],
    );

    // Two independent handlers: the selects no longer mirror each other, and each
    // side invalidates only what it actually affects.
    const handleOriginCurrencyChange = useCallback(
      (next: string) => {
        // The select may fire onChange even when re-selecting the same option.
        if (formData.origin_currency === next) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        updateField("origin_currency", next);
        // The amounts were denominated in the previous currency.
        updateField("amount_origin", "");
        updateField("amount_destination", "");
        clearQuoteData();
        // The beneficiary is untouched: the destination corridor did not change, so
        // the account and its currency are still valid.
      },
      [formData.origin_currency, updateField, clearQuoteData],
    );

    const handleDestinationChange = useCallback(
      (next: string) => {
        if (formData.destination === next) return;
        if (debounceRef.current) clearTimeout(debounceRef.current);
        updateField("destination", next);
        updateField("amount_origin", "");
        updateField("amount_destination", "");
        // Full recipient reset: typed fields AND client/account selection — a stale account id from the
        // previous corridor's currency would still pass the step-2 "account required" validation while
        // the account selector renders empty.
        clearRecipientDataForCorridorChange();
        clearQuoteData();
      },
      [formData.destination, updateField, clearRecipientDataForCorridorChange, clearQuoteData],
    );

    // Origin options are the org's ACTIVATED wallets: there is nothing to spend
    // from a wallet that does not exist. INTERNAL staff have no wallet list of
    // their own, so they fall back to the activation catalog.
    const originOptions = useMemo(() => {
      const source = usesRuntimeBalance
        ? Object.values(runtimeWalletsByCurrency)
            .filter((w): w is RuntimeWallet => !!w)
            .map((w) => ({ currency: w.currency, frozen: w.status === "frozen" }))
        : availableWalletCurrencies.map((currency) => ({ currency, frozen: false }));

      return source
        .sort((a, b) => a.currency.localeCompare(b.currency))
        .map(({ currency, frozen }) => ({
          value: currency,
          label: currencyLabel(currency),
          ...(frozen ? { badge: t("step1_origin_wallet_frozen_badge") } : {}),
        }));
    }, [usesRuntimeBalance, runtimeWalletsByCurrency, availableWalletCurrencies, t]);

    // Destinations stay the payout corridors of the runtime — that is what the rails and beneficiary forms
    // are built for — but a corridor whose route or fees are missing is dropped: offering it only leads to a
    // `no_route` / `no_fee_config` after the beneficiary form is filled in. One in maintenance stays, tagged,
    // so picking it says why instead of the country vanishing. The catalog fails open, so an unreadable one
    // leaves the full list exactly as before.
    const destinationOptions = useMemo(
      () =>
        countries
          .filter((c) => isRuntimeCorridor(c.code))
          .flatMap((c) => {
            const corridor = resolveRuntimeCorridor(c.code);
            if (!corridor) return [];
            const inMaintenance = corridors.inMaintenance({ country: corridor, direction: "PAYOUT" });
            // ANY of the corridor's rails keeps the country offered (Colombia has two) —
            // the step-2 panel disables the specific rail that is down.
            const available = RAILS_BY_RUNTIME_CORRIDOR[corridor].some((rail) =>
              corridors.isAvailable(rail, corridor, CURRENCY_BY_RUNTIME_CORRIDOR[corridor], "PAYOUT"),
            );
            if (!available && !inMaintenance) return [];
            return [
              {
                value: c.code,
                label: `${c.name} · ${c.currency}`,
                ...(inMaintenance ? { badge: tMaintenance("badge") } : {}),
              },
            ];
          }),
      [countries, corridors, tMaintenance],
    );
    const destinationInMaintenance =
      !!dest && corridors.inMaintenance({ country: dest.code, direction: "PAYOUT" });

    const originSuffix = originCurrency ? `${currencySymbol(originCurrency)} ${originCurrency}`.trim() : "";
    const destSuffix = dest ? `${dest.currencySymbol} ${dest.currency}` : "";
    const sendLoading = quoteLoading && lastChangedAmountField === "destination";
    const recvLoading = quoteLoading && lastChangedAmountField === "origin";

    const routeSelects = (
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        <div className="flex flex-col gap-2">
          <FieldSelect
            id="origen"
            label={t("step1_origin_label")}
            placeholder={t("step1_select_placeholder")}
            value={formData.origin_currency}
            disabled={isOriginLocked}
            options={originOptions}
            onValueChange={handleOriginCurrencyChange}
          />
          {usesRuntimeBalance && originOptions.length === 0 && !isWalletListLoading && (
            <div className="text-xs text-muted-foreground">{t("step1_no_wallets_note")}</div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <FieldSelect
            id="destino"
            label={t("step1_destination_label")}
            placeholder={t("step1_select_placeholder")}
            value={formData.destination}
            options={destinationOptions}
            onValueChange={handleDestinationChange}
          />
          {dest && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className="inline-block size-1.5 rounded-full bg-brand-700" />
              {t("rail_label")}: {rail}
            </div>
          )}
          {dest && originCurrency && (
            <div className="text-xs text-muted-foreground">
              {isCrossCurrency
                ? t("step1_cross_currency_hint", {
                    origin: originCurrency,
                    destination: dest.currency,
                  })
                : t("step1_same_currency_hint", { currency: dest.currency })}
            </div>
          )}
        </div>
      </div>
    );

    if (destinationInMaintenance && dest) {
      return (
        <div className="flex flex-col gap-[22px] text-foreground">
          {routeSelects}
          <RailMaintenanceNotice kind="send" country={dest.code} />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-[22px] text-foreground">
        {/* Origen / Destino */}
        {routeSelects}

        {/* Cuenta de origen */}
        {originCurrency && usesRuntimeBalance && (
          <div className="flex flex-col gap-3.5 rounded-xl border border-border bg-muted px-[22px] py-[18px]">
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col gap-1.5">
                <div className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
                  {t("step1_origin_account_title")}
                </div>
                <div className="text-sm text-muted-foreground">{t("step1_available_balance")}</div>
                {showBalanceError ? (
                  <div className="flex flex-col items-start gap-1.5">
                    <div className="flex items-center gap-1.5 text-sm text-danger-container-foreground">
                      <span className="inline-flex size-[15px] items-center justify-center rounded-full bg-danger-container text-[10px] font-bold text-danger-container-foreground">
                        !
                      </span>
                      {t("runtime_balance_error", { currency: originCurrency })}
                    </div>
                    <button
                      type="button"
                      onClick={() => originCurrency && fetchRuntimeWallet(originCurrency)}
                      className="text-sm font-semibold text-brand-700 hover:underline"
                    >
                      {t("button_retry")}
                    </button>
                  </div>
                ) : runtimeLoading && !runtimeWallet ? (
                  <div className="flex items-center gap-2 text-base text-muted-foreground">
                    <span
                      aria-hidden="true"
                      className="size-4 animate-spin rounded-full border-2 border-border border-t-primary"
                    />
                    {t("loading_balance")}
                  </div>
                ) : (
                  <div className="text-2xl font-bold tracking-tight tabular-nums">
                    {currencySymbol(originCurrency)} {displayBalance}{" "}
                    <span className="text-sm font-semibold text-muted-foreground">{originCurrency}</span>
                  </div>
                )}
              </div>
              <button
                type="button"
                aria-label={t("step1_refresh_balance")}
                title={t("step1_refresh_balance")}
                onClick={() => {
                  if (balanceSpin) return;
                  setBalanceSpin(true);
                  if (usesRuntimeBalance && originCurrency) fetchRuntimeWallet(originCurrency);
                  setTimeout(() => setBalanceSpin(false), 800);
                }}
                className={cn(
                  "flex size-[34px] shrink-0 items-center justify-center rounded-full border border-border bg-card text-base text-foreground hover:bg-muted",
                  balanceSpin && "animate-[spin_.8s_ease-out]",
                )}
              >
                ↻
              </button>
            </div>
            {usesRuntimeBalance && runtimeWallet?.status === "frozen" && (
              <div
                aria-live="polite"
                className="rounded-[9px] border border-warning-container-foreground/20 bg-warning-container px-3.5 py-2 text-sm text-warning-container-foreground"
              >
                {t("runtime_wallet_frozen_warning", { currency: runtimeWallet.currency })}
              </div>
            )}
          </div>
        )}

        {/* Monto dual */}
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-1 items-end gap-2.5 sm:grid-cols-[1fr_40px_1fr]">
            <HeroAmountInput
              id="envia"
              label={t("send_label")}
              suffix={originSuffix}
              loading={sendLoading}
              error={!!amountError}
              placeholder={t("step1_amount_placeholder")}
              value={formData.amount_origin}
              onValueChange={(value) => handleAmountChange("send", value)}
            />
            <div
              aria-hidden="true"
              className="mx-auto flex size-9 shrink-0 items-center justify-center rounded-full border-[1.5px] border-border bg-card text-primary max-sm:my-0.5 max-sm:rotate-90 sm:mb-2"
            >
              <ArrowLeftRight className="size-4" />
            </div>
            <HeroAmountInput
              id="recibe"
              label={t("quote_recipient_receives_label")}
              suffix={destSuffix}
              loading={recvLoading}
              error={!!destinationError}
              placeholder={t("step1_amount_placeholder")}
              value={formData.amount_destination}
              onValueChange={(value) => handleAmountChange("recv", value)}
            />
          </div>
          <div className="text-center text-xs text-muted-foreground">{t("step1_amounts_hint")}</div>
          {limitHint && <div className="text-center text-xs text-muted-foreground">{limitHint}</div>}
          {[amountError, destinationError].filter(Boolean).map((message) => (
            <div
              key={message}
              aria-live="polite"
              className="flex items-center gap-1.5 text-sm text-danger-container-foreground"
            >
              <span className="inline-flex size-[15px] items-center justify-center rounded-full bg-danger-container text-[10px] font-bold text-danger-container-foreground">
                !
              </span>
              {message}
            </div>
          ))}
        </div>

        {/* Cotización — cargando */}
        {quoteLoading && (
          <div className="flex animate-[pulse_1.4s_ease-in-out_infinite] flex-col gap-3 rounded-xl border border-border px-[22px] py-[18px]">
            <div className="text-sm text-muted-foreground">{t("step1_quote_loading")}</div>
            <div className="h-3 w-[55%] rounded-md bg-muted" />
            <div className="h-3 w-[40%] rounded-md bg-muted" />
            <div className="h-5 w-[70%] rounded-md bg-primary/10" />
          </div>
        )}

        {/* Cotización — error */}
        {quoteError && !quoteLoading && (
          <div className="flex items-center justify-between gap-3 rounded-xl border border-danger/20 bg-danger-container px-5 py-4">
            {/* An out-of-range amount is not a pricing failure: the backend's sentence names the bound. */}
            <div className="text-sm text-danger-container-foreground">
              {isAmountLimitCode(quoteErrorCode) ? quoteError : t("step1_quote_error")}
            </div>
            <ColocaButton
              variant="destructive"
              size="sm"
              className="shrink-0"
              onClick={() => {
                const editedSide = lastChangedAmountField === "destination" ? "recv" : "send";
                const raw = editedSide === "send" ? formData.amount_origin : formData.amount_destination;
                const amount = parseAmountInputDecimal(raw);
                const corridor = resolveRuntimeCorridor(formData.destination);
                if (amount === null || !corridor || !formData.origin_currency) return;
                const params: DispersionQuoteParams = {
                  originCurrency: formData.origin_currency,
                  destinationCountry: corridor,
                  rail: railForQuote(corridor, formData.recipientFields),
                };
                if (editedSide === "send") params.sendAmount = amount;
                else params.receiveAmount = amount;
                fetchQuote(params);
              }}
            >
              {t("button_retry")}
            </ColocaButton>
          </div>
        )}

        {/* Cotización — lista (countdown tick confined inside the card) */}
        {quoteData && !quoteLoading && !amountError && <QuoteSummaryCard quote={quoteData} dest={dest} />}
      </div>
    );
  },
);
